import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Space,
  Select,
  Form,
  Input,
  DatePicker,
  Button,
  Row,
  Col,
  Collapse,
  Upload,
  Typography,
  Spin,
  Descriptions,
  Divider,
  Checkbox,
  Switch,
  Modal,
} from 'antd';
import { fieldToPanelMappingForIndividualTrader, uploadFields } from './FormFields';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  UploadOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import IndividualTraderService from '../../services/IndividualTraderService';
import generateUploadUrlService from '../../services/GenerateUploadUrlService';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { getFileIconByURL } from '../../util/Common';
import WorkflowAccOpeningService from '../../services/WorkflowAccOpeningService';
import WorkflowAccModificationService from '../../services/WorkflowAccModificationService';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Panel } = Collapse;
const { Text, Title } = Typography;

export default function FillIndividualTrader({ type }) {
  // React hook
  const { traderId } = useParams();
  const navigate = useNavigate();
  const [traderInformation, setTraderInformation] = useState(null);
  const { showNotification, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activePanels, setActivePanels] = useState(['1']);
  const [isSaved, setIsSaved] = useState(true);

  const handleFinishFailed = ({ errorFields }) => {
    console.log(errorFields);
    if (errorFields.length > 0) {
      const firstErrorField = errorFields[0].name[0];
      const errorPanel = fieldToPanelMappingForIndividualTrader[firstErrorField];
      console.log(errorPanel);
      setActivePanels([errorPanel]);
    }
  };

  // Form control dependency from ant design
  const [form] = Form.useForm();
  const [requiredMark, setRequiredMarkType] = useState('');
  const onRequiredTypeChange = ({ requiredMarkValue }) => {
    setRequiredMarkType(requiredMarkValue);
    setIsSaved(false);
  };
  console.log(type);
  const WorkflowService =
    type === 'modification' ? WorkflowAccModificationService : WorkflowAccOpeningService;

  // Get individual trader with id
  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await WorkflowService.getIndividualAcc({
        workflowId: traderId,
        // checkerId: user.userId,
      });
      console.log(result);
      if (!result.success) {
        throw new Error(result.message);
      }
      result.data.individualAccountOpeningData =
        type === 'modification'
          ? result.data.individualAccountModificationData
          : result.data.individualAccountOpeningData;

      result.data.individualAccountOpeningData.stage =
        type === 'modification'
          ? result.data.individualAccountModificationData.workflowStage
          : result.data.individualAccountOpeningData.stage;
      renderData(result.data);
      setTraderInformation(result.data);
    } catch (error) {
      showNotification('error', error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Set existing data into form items
  const renderData = data => {
    form.setFieldsValue({
      primaryEmail: data.individualAccountOpeningData.primaryEmail,
      ccEmail: data.ccEmail,
      bccEmail: data.bccEmail,
      traderIdentityType: data.individualProfile.identityType,
      traderIdNumber: data.individualProfile.identityValue,
      traderNameOnID: data.individualProfile.nameOnIdentity,
      traderFirstName: data.individualProfile.firstName,
      traderMiddleName:
        data.individualProfile.middleName === null ? '' : data.individualProfile.middleName,
      traderLastName: data.individualProfile.lastName,
      traderNationalityType: data.individualProfile.nationalityType,
      traderDOB: data.individualProfile.dateOfBirth
        ? dayjs.unix(data.individualProfile.dateOfBirth)
        : null,
      traderMaritialStatus: data.individualProfile.maritalStatus,
      traderOccupation: data.individualProfile.occupation,
      traderPoliticalAffiliation: data.individualProfile.politicalAffiliation,
      traderAnnualIncomeRange: data.individualProfile.annualIncomeRange,
      traderAnnualIncomeOnDate: data.individualProfile.annualIncomeOnDate
        ? dayjs.unix(data.individualProfile.annualIncomeOnDate)
        : null,
      traderNetWorth: data.individualProfile.netWorth,
      traderNetWorthOnDate: data.individualProfile.netWorthOnDate
        ? dayjs.unix(data.individualProfile.netWorthOnDate)
        : null,
      traderSourceOfFund: data.individualProfile.sourceOfFund,
      // traderNextOfKinIdType: data.individualProfile.nextOfKinIdentityType,
      // traderNextOfKinIdNumber: data.individualProfile.nextOfKinIdentityValue,
      // traderNextOfKinType: data.individualProfile.nextOfKinType,
      // traderNextOfKinName: data.individualProfile.nextOfKinName,
      traderInPersonVerification: data.individualProfile.inPersonVerification,
      tradingAccountType: data.tradingBankAccount.accountType,
      tradingAccountNumber: data.tradingBankAccount.accountNo,
      tradingSwiftCode: data.tradingBankAccount.bankSwiftCode,
      tradingBankCode: data.tradingBankAccount.bankCode,
      tradingBankName: data.tradingBankAccount.bankName,
      tradingBranchCode: data.tradingBankAccount.bankBranchCode,
      tradingBranchName: data.tradingBankAccount.bankBranchName,
      tradingAddress: data.tradingBankAccount.address,
      tradingTownship: data.tradingBankAccount.township,
      tradingCity: data.tradingBankAccount.city,
      tradingState: data.tradingBankAccount.state,
      tradingCountry: data.tradingBankAccount.country,
      tradingPostalCode: data.tradingBankAccount.postalCode,
      tradingPOA: data.tradingBankAccount.poa ? data.tradingBankAccount.poa : false,
      tradingPhoneNumber: data.tradingBankAccount.phoneNo,
      tradingPoaAgreementDate: data.tradingBankAccount.poaAgreementDate
        ? dayjs.unix(data.tradingBankAccount.poaAgreementDate)
        : null,
      personalAccountType: data.personalBankAccount.accountType,
      personalAccountNumber: data.personalBankAccount.accountNo,
      personalSwiftCode: data.personalBankAccount.bankSwiftCode,
      personalBankCode: data.personalBankAccount.bankCode,
      personalBankName: data.personalBankAccount.bankName,
      personalBranchCode: data.personalBankAccount.bankBranchCode,
      personalBranchName: data.personalBankAccount.bankBranchName,
      personalAddress: data.personalBankAccount.address,
      personalPostalCode: data.personalBankAccount.postalCode,
      personalTownship: data.personalBankAccount.township,
      personalCity: data.personalBankAccount.city,
      personalState: data.personalBankAccount.state,
      personalCountry: data.personalBankAccount.country,
      personalPhoneNumber: data.personalBankAccount.phoneNo,
      personalPOA: data.personalBankAccount.poa ? data.personalBankAccount.poa : false,
      personalPoaAgreementDate: data.personalBankAccount.poaAgreementDate
        ? dayjs.unix(data.personalBankAccount.poaAgreementDate)
        : null,
      communicationStreet: data.communicationAddress.street,
      communicationTownship: data.communicationAddress.township,
      communicationCity: data.communicationAddress.city,
      communicationState: data.communicationAddress.state,
      communicationCountry: data.communicationAddress.country,
      communicationPhoneNumber: data.communicationAddress.phoneNo,
      communicationMobileNumber: data.communicationAddress.mobileNo,
      communicationFaxNumber: data.communicationAddress.fax,
      communicationIsdCode: data.communicationAddress.isdCode,
      communicationPostalCode: data.communicationAddress.postalCode,
      communicationStdCode: data.communicationAddress.stdCode,
      communicationResident: data.communicationAddress.resident
        ? data.communicationAddress.resident
        : false,
      communicationProof: data.communicationAddress.proof,
      permanentStreet: data.permanentAddress.street,
      permanentTownship: data.permanentAddress.township,
      permanentCity: data.permanentAddress.city,
      permanentState: data.permanentAddress.state,
      permanentCountry: data.permanentAddress.country,
      permanentPhoneNumber: data.permanentAddress.phoneNo,
      permanentMobileNumber: data.permanentAddress.mobileNo,
      permanentFaxNumber: data.permanentAddress.fax,
      permanentIsdCode: data.permanentAddress.isdCode,
      permanentPostalCode: data.permanentAddress.postalCode,
      permanentStdCode: data.permanentAddress.stdCode,
      permanentResident: data.permanentAddress.resident ? data.permanentAddress.resident : false,
      permanentProof: data.permanentAddress.proof,
      applicationForm: data.individualProofDocument.applicationFormKey,
      bankStatement: data.individualProofDocument.bankStatementKey,
      communicationAddress: data.individualProofDocument.communicationAddressKey,
      householdList: data.individualProofDocument.houseHoldListKey,
      kycApplication: data.individualProofDocument.kycApplicationKey,
      primaryIdentityPhoto: data.individualProofDocument.primaryIdentityPhotoKey,
      secondaryIdentityPhoto: data.individualProofDocument.secondaryIdentityPhotoKey,
      signatureProof: data.individualProofDocument.signatureProofKey,
    });
  };

  // Form save
  const handleSave = async () => {
    const ccEmail = form.getFieldValue('ccEmail');
    const bccEmail = form.getFieldValue('bccEmail');
    const primaryEmail = form.getFieldValue('primaryEmail');
    console.log(form.getFieldValue('traderNextOfKinName'));

    const payload = {
      ccEmail,
      bccEmail,
      primaryEmail,
      workflowId: traderId,
      individualProfile: {
        identityType: form.getFieldValue('traderIdentityType') || null,
        identityValue: form.getFieldValue('traderIdNumber') || null,
        nameOnIdentity: form.getFieldValue('traderNameOnID') || null,
        firstName: form.getFieldValue('traderFirstName') || null,
        middleName: form.getFieldValue('traderMiddleName') || '',
        lastName: form.getFieldValue('traderLastName') || null,
        nationalityType: form.getFieldValue('traderNationalityType') || null,
        dateOfBirth: form.getFieldValue('traderDOB') || null,
        maritalStatus: form.getFieldValue('traderMaritialStatus') || null,
        occupation: form.getFieldValue('traderOccupation') || null,
        politicalAffiliation: form.getFieldValue('traderPoliticalAffiliation') || null,
        annualIncomeRange: form.getFieldValue('traderAnnualIncomeRange') || null,
        annualIncomeOnDate: form.getFieldValue('traderAnnualIncomeOnDate') || null,
        netWorth: form.getFieldValue('traderNetWorth') || null,
        netWorthOnDate: form.getFieldValue('traderNetWorthOnDate') || null,
        sourceOfFund: form.getFieldValue('traderSourceOfFund') || null,
        // nextOfKinIdentityType: form.getFieldValue('traderNextOfKinIdType') || null,
        // nextOfKinIdentityValue: form.getFieldValue('traderNextOfKinIdNumber') || null,
        // nextOfKinType: form.getFieldValue('traderNextOfKinType') || null,
        // nextOfKinName: form.getFieldValue('traderNextOfKinName') || null,
        inPersonVerification: form.getFieldValue('traderInPersonVerification'),
      },
      tradingBankAccount: {
        accountNo: form.getFieldValue('tradingAccountNumber') || null,
        accountType: form.getFieldValue('tradingAccountType') || null,
        address: form.getFieldValue('tradingAddress') || null,
        bankBranchCode: form.getFieldValue('tradingBranchCode') || null,
        bankBranchName: form.getFieldValue('tradingBranchName') || null,
        bankCode: form.getFieldValue('tradingBankCode') || null,
        bankName: form.getFieldValue('tradingBankName') || null,
        bankSwiftCode: form.getFieldValue('tradingSwiftCode') || null,
        city: form.getFieldValue('tradingCity') || null,
        country: form.getFieldValue('tradingCountry') || null,
        phoneNo: form.getFieldValue('tradingPhoneNumber') || null,
        poa: form.getFieldValue('tradingPOA'),
        poaAgreementDate: form.getFieldValue('tradingPoaAgreementDate') || null,
        postalCode: form.getFieldValue('tradingPostalCode') || null,
        state: form.getFieldValue('tradingState') || null,
        township: form.getFieldValue('tradingTownship') || null,
      },
      personalBankAccount: {
        accountNo: form.getFieldValue('personalAccountNumber') || null,
        accountType: form.getFieldValue('personalAccountType') || null,
        address: form.getFieldValue('personalAddress') || null,
        bankBranchCode: form.getFieldValue('personalBranchCode') || null,
        bankBranchName: form.getFieldValue('personalBranchName') || null,
        bankCode: form.getFieldValue('personalBankCode') || null,
        bankName: form.getFieldValue('personalBankName') || null,
        bankSwiftCode: form.getFieldValue('personalSwiftCode') || null,
        city: form.getFieldValue('personalCity') || null,
        country: form.getFieldValue('personalCountry') || null,
        phoneNo: form.getFieldValue('personalPhoneNumber') || null,
        poa: form.getFieldValue('personalPOA'),
        poaAgreementDate: form.getFieldValue('personalPoaAgreementDate') || null,
        postalCode: form.getFieldValue('personalPostalCode') || null,
        state: form.getFieldValue('personalState') || null,
        township: form.getFieldValue('personalTownship') || null,
      },
      communicationAddress: {
        city: form.getFieldValue('communicationCity') || null,
        country: form.getFieldValue('communicationCountry') || null,
        fax: form.getFieldValue('communicationFaxNumber') || null,
        isdCode: form.getFieldValue('communicationIsdCode') || null,
        mobileNo: form.getFieldValue('communicationMobileNumber') || null,
        phoneNo: form.getFieldValue('communicationPhoneNumber') || null,
        postalCode: form.getFieldValue('communicationPostalCode') || null,
        proof: form.getFieldValue('communicationProof') || null,
        resident: form.getFieldValue('communicationResident'),
        state: form.getFieldValue('communicationState') || null,
        stdCode: form.getFieldValue('communicationStdCode') || null,
        street: form.getFieldValue('communicationStreet') || null,
        township: form.getFieldValue('communicationTownship') || null,
      },
      permanentAddress: {
        city: form.getFieldValue('permanentCity') || null,
        country: form.getFieldValue('permanentCountry') || null,
        fax: form.getFieldValue('permanentFaxNumber') || null,
        isdCode: form.getFieldValue('permanentIsdCode') || null,
        mobileNo: form.getFieldValue('permanentMobileNumber') || null,
        phoneNo: form.getFieldValue('permanentPhoneNumber') || null,
        postalCode: form.getFieldValue('permanentPostalCode') || null,
        proof: form.getFieldValue('permanentProof') || null,
        resident: form.getFieldValue('permanentResident'),
        state: form.getFieldValue('permanentState') || null,
        stdCode: form.getFieldValue('permanentStdCode') || null,
        street: form.getFieldValue('permanentStreet') || null,
        township: form.getFieldValue('permanentTownship') || null,
      },
      individualProofDocument: {
        applicationFormKey: form.getFieldValue('applicationFormKey') || null,
        bankStatementKey: form.getFieldValue('bankStatement') || null,
        communicationAddressKey: form.getFieldValue('communicationAddress') || null,
        houseHoldListKey: form.getFieldValue('householdList') || null,
        kycApplicationKey: form.getFieldValue('kycApplication') || null,
        primaryIdentityPhotoKey: form.getFieldValue('primaryIdentityPhoto') || null,
        secondaryIdentityPhotoKey: form.getFieldValue('secondaryIdentityPhoto') || null,
        signatureProofKey: form.getFieldValue('signatureProofKey') || null,
      },
    };

    setLoading(true);
    const result = await WorkflowService.saveIndividualAcc(payload);
    console.log(result);
    if (result.success) {
      showNotification('success', 'Saved');
      setIsSaved(true);
    } else {
      showNotification('error', result.message);
    }
    setLoading(false);
  };

  // Form submit
  const handleSubmit = async traderId => {
    // if (!isSaved) {
    //   showNotification('error', 'Please save the changes values before submit.');
    //   return;
    // }
    console.log(form.getFieldsValue(true));
    setLoading(true);
    const result = await WorkflowService.submitIndividualAcc({ workflowId: traderId });

    if (result.success) {
      showNotification('success', 'Submitted');
      navigate(type === 'modification' ? '/modification' : '/');
    } else {
      showNotification('error', result.message);
    }
    setLoading(false);
    handleCancel();
  };

  // Approve trader
  const handleApprove = async traderId => {
    setLoading(true);
    const result = await WorkflowService.approveIndividualAcc({ workflowId: traderId });
    if (result.success) {
      showNotification('success', 'Approved');
      navigate('/individual');
    } else {
      showNotification('error', result.message);
    }
    setLoading(false);
    handleCancel();
  };

  const handleDelete = async traderId => {
    setLoading(true);
    const result = await WorkflowService.deleteAcc({ workflowId: traderId });
    if (result.success) {
      showNotification('success', 'Deleted');
      navigate(`/${type === 'modification' ? type : ''}`);
    } else {
      showNotification('error', result.message);
    }
    setLoading(false);
    handleCancel();
  };

  const handleUpload = async (options, documentType, name) => {
    const { file, onSuccess, onError, onProgress } = options;

    const filetype = file.type;
    const fileInfoArr = file.name.split('.');
    const extension = fileInfoArr[fileInfoArr.length - 1];

    try {
      // 1. Get the presigned URL from API - upload-xxx-document
      const response = await WorkflowService.generateIndividualDocument({
        workflowId: traderId,
        extension: extension,
        individualDocumentType: documentType,
      });

      if (!response.success) {
        showNotification('error', response.message);
        return;
      }

      // 2. Upload the file to S3
      try {
        const resp = await axios.put(response.uploadUrl, file, {
          onUploadProgress: progressEvent => {
            const { loaded, total } = progressEvent;
            const percent = Math.round((loaded / total) * 100);
            onProgress({ percent });
          },
          headers: {
            'Content-Type': filetype,
          },
        });

        onSuccess(resp.data, file);
        form.setFieldsValue({
          [name]: response.uploadUrl,
        });

        form.validateFields([name]);
      } catch (error) {
        onError(error);
        showNotification('error', 'File upload failed');
      }
    } catch (error) {
      showNotification('error', error.message);
    }
  };

  const maritalStatusOptions = ['Married', 'Single', 'Widow/Widower', 'Divorce', 'Not Applicable'];
  const annualIncomeRangeOptions = [
    'Below & Equal to MMK 2,000,000',
    'From MMK  2,000,001 to MMK 5,000,000',
    'From MMK  5,000,001 to MMK 10,000,000',
    'From MMK  10,000,001 to MMK 20,000,000',
    'From MMK  20,000,001 to MMK 30,000,000',
    'Above MMK 30,000,000',
  ];
  const netWorthOptions = [
    'Below & Equal to MMK 10,000,000',
    'From MMK 10,000,001 to MMK 50,000,000',
    'From MMK 50,000,001 to MMK 100,000,000',
    'Above MMK 100,000,000',
  ];
  const sourceOfFundOptions = ['Employment', 'Self-employed', 'Inheritance', 'Others'];
  const nextOfKinTypeOptions = ['FATHER', 'MOTHER', 'SPOUSE', 'FRIEND', 'RELATIVE'];
  const occupationInputRef = useRef(null);
  const [occupation, setOccupation] = useState('');

  const [occupationItems, setOccupationItems] = useState([
    'Business',
    'Private Sector',
    'Public Sector',
    'Government Services',
    'Famer',
    'Student',
    'Professional',
    'Housewife',
    'Retired',
    'Salary Employee',
  ]);
  const addOccupation = e => {
    e.preventDefault();
    setOccupationItems([...occupationItems, occupation]);
    setOccupation('');
    setTimeout(() => {
      occupationInputRef.current?.focus();
    }, 0);
  };
  const onOccupationChange = event => {
    setOccupation(event.target.value);
  };

  const goBack = () => {
    navigate(-1); // Go back one page in history
  };

  const handleModal = async type => {
    switch (type) {
      case 'submit':
        if (!isSaved) {
          showNotification('error', 'Please save the changes values before submit.');
          return;
        }
        await handleSubmit(traderId);
        break;
      case 'approve':
        await handleApprove(traderId);
        break;
      case 'delete':
        await handleDelete(traderId);
        break;
      default:
        break;
    }
  };

  const [clickType, setClickType] = useState('');

  const handleCancel = () => {
    setClickType('');
  };

  return (
    <>
      {/* <Space
        direction="horizontal"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
        }}
      >
        
      </Space> */}
      <Modal
        title={traderInformation?.individualAccountOpeningData.tradingAccountNo}
        open={clickType.length === 0 ? false : true}
        onOk={() => handleModal(clickType)}
        confirmLoading={loading}
        onCancel={handleCancel}
      >
        <p>{'Are you sure to ' + clickType + ' this account'}</p>
      </Modal>
      <Title level={4} style={{ margin: '0 0 2rem 0', textTransform: 'capitalize' }}>
        Individual Account {type} - Submission
      </Title>
      <Card title="Step 2 of 2: Filling" style={{ background: '#fcfcfc', width: '100%' }}>
        <Space direction="vertical" size={'middle'} style={{ width: '100%' }}>
          {/* {traderInformation != null ? ( */}
          <Descriptions
            title={<Text>Trader Information</Text>}
            size={'middle'}
            column={{ xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
          >
            <Descriptions.Item label="Application No">
              <Text strong>{traderInformation?.applicationInfo.applicationNo}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Exchange">
              <Text strong>{traderInformation?.individualAccountOpeningData.exchangeSegment}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Agreement Date">
              <Text strong>
                {dayjs
                  .unix(traderInformation?.applicationInfo.agreementDate)
                  .format('DD-MMMM-YYYY')}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Identity Type">
              <Text strong>{traderInformation?.individualProfile.identityType}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Nationality Type">
              <Text strong>{traderInformation?.individualProfile.nationalityType}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Name on Identity">
              <Text strong>{traderInformation?.individualProfile.nameOnIdentity}</Text>
            </Descriptions.Item>
          </Descriptions>
          {/* ) : null} */}
          <Divider />
          <Form
            onFinish={() => setClickType('submit')}
            onFinishFailed={handleFinishFailed}
            form={form}
            labelCol={{
              span: 8,
              lg: {
                span: 12,
              },
              xl: {
                span: 10,
              },
            }}
            labelWrap={true}
            layout="horizontal"
            size={'middle'}
            // required field related
            initialValues={{
              requiredMarkValue: requiredMark,
              // tradingAccountType: 'CURRENT_ACCOUNT',
              // personalAccountType: 'SAVING_ACCOUNT',
            }}
            onValuesChange={onRequiredTypeChange}
            requiredMark={requiredMark}
          >
            <Collapse
              defaultActiveKey={['1']}
              bordered={false}
              expandIconPosition={'end'}
              accordion
              activeKey={activePanels}
              onChange={setActivePanels}
            >
              {/* Emails */}
              <Panel header={'Email'} key={'1'} forceRender>
                <Row gutter={16} style={{ marginTop: '2rem' }}>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Form.Item label="Primary Email" name={'primaryEmail'}>
                      <Input />
                    </Form.Item>
                    <Form.Item label="CC Email" name={'ccEmail'}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Form.Item label="BCC Email" name={'bccEmail'}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>

              {/* Trader Profile */}
              <Panel header={'Trader Profile'} key={'2'} forceRender>
                <Row gutter={16} style={{ marginTop: '2rem' }}>
                  <Col
                    xs={{
                      span: 24,
                      // order: 2,
                    }}
                    sm={24}
                    md={24}
                    lg={{
                      span: 12,
                      // order: 1,
                    }}
                  >
                    <Form.Item
                      label="Identity Type"
                      name={'traderIdentityType'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Select>
                        <Select.Option value="NRIC">NRIC</Select.Option>
                        <Select.Option value="PASSPORT">PASSPORT</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      label="Nationality Type"
                      name={'traderNationalityType'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Select>
                        <Select.Option value="MYANMAR">MYANMAR</Select.Option>
                        <Select.Option value="OTHER">OTHER</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      label="Identity No"
                      name={'traderIdNumber'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label="Name on Identity"
                      name={'traderNameOnID'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label="First Name"
                      name={'traderFirstName'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item label="Middle Name" name={'traderMiddleName'}>
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label="Last Name"
                      name={'traderLastName'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label="Date of Birth"
                      name={'traderDOB'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <DatePicker
                        style={{
                          width: '100%',
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col
                    xs={{
                      span: 24,
                      // order: 1,
                    }}
                    sm={24}
                    md={24}
                    lg={{
                      span: 12,
                      // order: 2,
                    }}
                  >
                    <Form.Item
                      label="Maritial Status"
                      name={'traderMaritialStatus'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Select
                        options={maritalStatusOptions.map(item => ({
                          label: item,
                          value: item,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Occupation"
                      name={'traderOccupation'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Select
                        // style={{
                        //   width: 300,
                        // }}
                        dropdownRender={menu => (
                          <>
                            {menu}
                            <Divider
                              style={{
                                margin: '8px 0',
                              }}
                            />
                            <Space
                              style={{
                                padding: '0 8px 4px',
                              }}
                            >
                              <Input
                                ref={occupationInputRef}
                                value={occupation}
                                onChange={onOccupationChange}
                              />
                              <Button type="text" icon={<PlusOutlined />} onClick={addOccupation}>
                                Add Item
                              </Button>
                            </Space>
                          </>
                        )}
                        options={occupationItems.map(item => ({
                          label: item,
                          value: item,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Political Affiliation"
                      name={'traderPoliticalAffiliation'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label="Annual Income Range"
                      name={'traderAnnualIncomeRange'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Select
                        options={annualIncomeRangeOptions.map(item => ({
                          label: item,
                          value: item,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Annual Income On Date"
                      name={'traderAnnualIncomeOnDate'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <DatePicker
                        style={{
                          width: '100%',
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Net Worth"
                      name={'traderNetWorth'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Select
                        options={netWorthOptions.map(item => ({
                          label: item,
                          value: item,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Net Worth On Date"
                      name={'traderNetWorthOnDate'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <DatePicker
                        style={{
                          width: '100%',
                        }}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Source Of Fund"
                      name={'traderSourceOfFund'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Select
                        options={sourceOfFundOptions.map(item => ({
                          label: item,
                          value: item,
                        }))}
                      />
                    </Form.Item>
                    {/* <Form.Item
                      label="Next of Kin ID Type"
                      name={'traderNextOfKinIdType'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Select>
                        <Select.Option value="NRIC">NRIC</Select.Option>
                        <Select.Option value="PASSPORT">PASSPORT</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      label="Next of Kin ID No"
                      name={'traderNextOfKinIdNumber'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label="Next of Kin Type"
                      name={'traderNextOfKinType'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Select
                        options={nextOfKinTypeOptions.map(item => ({
                          label: item,
                          value: item,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Next of Kin Name"
                      name={'traderNextOfKinName'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item> */}
                    <Form.Item
                      label="Status (In Person Verification)"
                      name="traderInPersonVerification"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>

              {/* Trading Bank Account */}
              <Panel header={'Trading Bank Account'} key={'3'} forceRender>
                <Row gutter={[16, 16]} style={{ marginTop: '2rem' }}>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Form.Item
                      label="Account Type"
                      name={'tradingAccountType'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Select>
                        <Select.Option value="SAVING_ACCOUNT">SAVING_ACCOUNT</Select.Option>
                        <Select.Option value="CURRENT_ACCOUNT">CURRENT_ACCOUNT</Select.Option>
                        <Select.Option value="CASH_CURRENT_ACCOUNT">
                          CASH_CURRENT_ACCOUNT
                        </Select.Option>
                        <Select.Option value="TRADING_BANK_ACCOUNT">
                          TRADING_BANK_ACCOUNT
                        </Select.Option>
                        <Select.Option value="OTHERS">OTHERS</Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label="Account Number"
                      name={'tradingAccountNumber'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Swift Code"
                      name={'tradingSwiftCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Bank Code"
                      name={'tradingBankCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Bank Name"
                      name={'tradingBankName'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Branch Code"
                      name={'tradingBranchCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Branch Name"
                      name={'tradingBranchName'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Address"
                      name={'tradingAddress'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <TextArea rows={8} onChange={e => e.target.value} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Form.Item
                      label="Township"
                      name={'tradingTownship'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="City"
                      name={'tradingCity'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="State"
                      name={'tradingState'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Country"
                      name={'tradingCountry'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Postal Code"
                      name={'tradingPostalCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item label="POA" name="tradingPOA" valuePropName="checked">
                      <Checkbox></Checkbox>
                    </Form.Item>

                    <Form.Item
                      label="Phone No"
                      name={'tradingPhoneNumber'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="POA Agreement Date"
                      name={'tradingPoaAgreementDate'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <DatePicker
                        style={{
                          width: '100%',
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>

              {/* Personal Bank Account */}
              <Panel header={'Personal Bank Account'} key={'4'} forceRender>
                <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Form.Item
                      label="Account Type"
                      name={'personalAccountType'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Select>
                        <Select.Option value="SAVING_ACCOUNT">SAVING_ACCOUNT</Select.Option>
                        <Select.Option value="CURRENT_ACCOUNT">CURRENT_ACCOUNT</Select.Option>
                        <Select.Option value="CASH_CURRENT_ACCOUNT">
                          CASH_CURRENT_ACCOUNT
                        </Select.Option>
                        <Select.Option value="TRADING_BANK_ACCOUNT">
                          TRADING_BANK_ACCOUNT
                        </Select.Option>
                        <Select.Option value="OTHERS">OTHERS</Select.Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label="Account Number"
                      name={'personalAccountNumber'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Swift Code"
                      name={'personalSwiftCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Bank Code"
                      name={'personalBankCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Bank Name"
                      name={'personalBankName'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Branch Code"
                      name={'personalBranchCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Branch Name"
                      name={'personalBranchName'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Address"
                      name={'personalAddress'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <TextArea rows={8} onChange={e => e.target.value} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Form.Item
                      label="Township"
                      name={'personalTownship'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="City"
                      name={'personalCity'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="State"
                      name={'personalState'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Country"
                      name={'personalCountry'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Postal Code"
                      name={'personalPostalCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item label="POA" name="personalPOA" valuePropName="checked">
                      <Checkbox></Checkbox>
                    </Form.Item>
                    <Form.Item
                      label="Phone No"
                      name={'personalPhoneNumber'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      label="POA Agreement Date"
                      name={'personalPoaAgreementDate'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <DatePicker
                        style={{
                          width: '100%',
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>

              {/* Communication Address */}
              <Panel header={'Communication Address'} key={'5'} forceRender>
                <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Form.Item
                      label="Street"
                      name={'communicationStreet'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Township"
                      name={'communicationTownship'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="City"
                      name={'communicationCity'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="State"
                      name={'communicationState'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Country"
                      name={'communicationCountry'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Phone Number"
                      name={'communicationPhoneNumber'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      label="Mobile Number"
                      name={'communicationMobileNumber'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Form.Item
                      label="Fax Number"
                      name={'communicationFaxNumber'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="ISD Code"
                      name={'communicationIsdCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Postal Code"
                      name={'communicationPostalCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="STD Code"
                      name={'communicationStdCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Resident"
                      name="communicationResident"
                      valuePropName="checked"
                    >
                      <Checkbox></Checkbox>
                    </Form.Item>

                    <Form.Item
                      label="Proof"
                      name={'communicationProof'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>

              {/* Permanent Address */}
              <Panel header={'Permanent Address'} key={'6'} forceRender>
                <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Form.Item
                      label="Street"
                      name={'permanentStreet'}
                      rules={[
                        {
                          required: true,
                          message: 'Please do not leave blank',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Township"
                      name={'permanentTownship'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="City"
                      name={'permanentCity'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="State"
                      name={'permanentState'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Country"
                      name={'permanentCountry'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Phone Number"
                      name={'permanentPhoneNumber'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      label="Mobile Number"
                      name={'permanentMobileNumber'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={12}>
                    <Form.Item
                      label="Fax Number"
                      name={'permanentFaxNumber'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="ISD Code"
                      name={'permanentIsdCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Postal Code"
                      name={'permanentPostalCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="STD Code"
                      name={'permanentStdCode'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item label="Resident" name="permanentResident" valuePropName="checked">
                      <Checkbox name="permanentResident"></Checkbox>
                    </Form.Item>

                    <Form.Item
                      label="Proof"
                      name={'permanentProof'}
                      // rules={[
                      //   {
                      //     required: true,
                      //     message: 'Please do not leave blank',
                      //   },
                      // ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>
              {/* Proof Documents */}
              <Panel header={'Proof Documents'} key={'7'} forceRender>
                <Row gutter={[16, 16]} style={{ marginTop: '2rem' }}>
                  {uploadFields.map(uploadField => {
                    // {
                    //   console.log(
                    //     uploadField.label,
                    //     traderInformation?.individualProofDocument[uploadField.name],
                    //   );
                    // }
                    return (
                      <Col xs={24} sm={24} md={24} lg={12} key={uploadField.name}>
                        <Form.Item
                          label={
                            <span>
                              {uploadField.require && type !== 'modification' && (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    color: '#ff4d4f',
                                    fontFamily: '"SimSun, sans-serif"',
                                    transform: 'translateY(4px)',
                                    fontSize: '18px',
                                    lineHeight: '1',
                                    marginInlineEnd: '4px',
                                  }}
                                >
                                  *
                                </span>
                              )}
                              {uploadField.label}
                            </span>
                          }
                          name={uploadField.name}
                          valuePropName={'file'}
                          rules={[
                            // {
                            //   required: uploadField.require,
                            //   message: uploadField.message,
                            // },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (
                                  traderInformation?.individualProofDocument[uploadField.name] !==
                                    '' ||
                                  !uploadField.require ||
                                  type === 'modification'
                                ) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error(uploadField.message));
                              },
                            }),
                          ]}
                        >
                          {traderInformation &&
                          traderInformation.individualProofDocument &&
                          traderInformation.individualProofDocument[uploadField.name] ? (
                            <a
                              href={traderInformation.individualProofDocument[uploadField.name]}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {getFileIconByURL(
                                traderInformation.individualProofDocument[uploadField.name],
                              )}{' '}
                              {uploadField.label}
                            </a>
                          ) : // <img src={traderInformation.corporateProofDocument[uploadField.name]} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px' }} />
                          null}

                          <Upload.Dragger
                            multiple={false}
                            maxCount={1}
                            disabled={
                              traderInformation?.individualAccountOpeningData.stage === 'PREPARING'
                                ? false
                                : true
                            }
                            onChange={async info => {
                              if (info.file.status === 'done') {
                                showNotification(
                                  'success',
                                  `${info.file.name} file uploaded successfully`,
                                );
                                await fetchData();
                                form.validateFields([uploadField.name]);
                              } else if (info.file.status === 'error') {
                                showNotification('error', `${info.file.name} file upload failed.`);
                              }
                            }}
                            customRequest={async options =>
                              handleUpload(options, uploadField.documentType, uploadField.name)
                            }
                            // defaultFileList={[uploadUrl]}
                          >
                            <Text>
                              Drag and drop here <br /> - Or - <br />
                              <Button icon={<UploadOutlined />}>Upload</Button>
                            </Text>
                          </Upload.Dragger>
                        </Form.Item>
                      </Col>
                    );
                  })}
                </Row>
              </Panel>
            </Collapse>

            {/* Group of buttons */}
            <Space
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '2rem',
              }}
            >
              <Space size={'middle'}>
                <Button type="default" size={'large'} onClick={goBack}>
                  <ArrowLeftOutlined /> Back
                </Button>
                <Button
                  type="primary"
                  danger
                  size={'large'}
                  onClick={() => setClickType('delete')}
                  loading={loading}
                  style={
                    traderInformation &&
                    traderInformation.individualAccountOpeningData.stage !== 'APPROVED'
                      ? {}
                      : { display: 'none' }
                  }
                >
                  Delete
                </Button>
              </Space>
              <Space size={'middle'}>
                <Button
                  type="dashed"
                  key="submit"
                  // htmlType="submit"
                  onClick={() => {
                    handleSave();
                  }}
                  size={'large'}
                  style={
                    user.role == 'CHECKER' ||
                    (traderInformation &&
                      traderInformation.individualAccountOpeningData.stage !== 'PREPARING')
                      ? { display: 'none' }
                      : {}
                    // {display: }
                  }
                  loading={loading}
                >
                  Save
                </Button>
                <Button
                  type="primary"
                  size={'large'}
                  htmlType="submit"
                  // onClick={() => {
                  //   setClickType('submit');
                  // }}
                  loading={loading}
                  style={
                    user.role == 'CHECKER' ||
                    (traderInformation &&
                      traderInformation.individualAccountOpeningData.stage === 'SUBMITTED') ||
                    (traderInformation &&
                      traderInformation.individualAccountOpeningData.stage === 'APPROVED')
                      ? { display: 'none' }
                      : {}
                  }
                >
                  Submit
                </Button>
                <Button
                  type="primary"
                  size={'large'}
                  onClick={() => setClickType('approve')}
                  loading={loading}
                  style={
                    user.role != 'MAKER' &&
                    traderInformation &&
                    traderInformation.individualAccountOpeningData.stage === 'SUBMITTED'
                      ? {}
                      : { display: 'none' }
                  }
                >
                  Approve
                </Button>
              </Space>
            </Space>
          </Form>
        </Space>
      </Card>
    </>
  );
}
